import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SHARED_COMPONENTS } from '../../../shared/shared-imports';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../../core/services/api.service';
import { SharedService } from '../../../shared/services/shared.service';
import { LookupKey } from '../../../shared/constants/lookup-keys';
import { ApiEndPoints, ApiRoot } from '../../../shared/constants/api-endpoints';
import { Utils } from '../../../Utils/utils';
import { AccountModel } from '../../../shared/models/loginuser.model';
import { RoutesPath } from '../../../shared/constants/routes-path';
import { getInvoiceFields, InvoiceField } from './models/invoice.model';
import { PaymentStatementRequest } from './models/payment-request.model';
import { LovSelectionService } from '../../../core/services/lov-selection.service';
import { DaysList, StatementDaysList } from './models/days-list';
import * as XLSX from 'xlsx';
import { FieldValue } from '../../../shared/constants/services.enum';
import { LookupService } from '../../../core/services/lookup.service';
import { InlineCalendarComponent } from '../../../shared/components/inline-calendar/inline-calendar.component';

@Component({
  selector: 'app-payments',
  imports: [ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS, InlineCalendarComponent],
  templateUrl: './payments.component.html',
  styleUrl: './payments.component.scss'
})
export class PaymentsComponent {
  request: PaymentStatementRequest = new PaymentStatementRequest();
  lookupKey = LookupKey;
  loader: boolean = false;
  pageSize: any = 10;
  readonly exportPageSize: number = 3000;
  totalRecords: number = 0;
  pageIndex: number = 1;

  filterForm: FormGroup;
  payments: any = [];
  accountCSN: string = "";
  accountId: string = ""
  users: any = [];
  dateRanges: any = DaysList;

  filteredRows: any[] = [];
  invoiceFields: InvoiceField[] = getInvoiceFields();
  lang: string = "en";
  isStatementsPage: boolean = false;
  pageTitleKey: string = 'PAYMENTS.TITLE';
  tableHeadings: string[] = [];
  tableDataKeys: string[] = [];
  showTableActions: boolean = true;
  showTableActionMore: boolean = true;
  showTableActionPay: boolean = true;
  initialLoad: boolean = true;
  userType: string = ""
  today: Date = new Date();
  showDateFromCalendar: boolean = false;
  showDateToCalendar: boolean = false;
  selectedDateFrom: Date | null = null;
  selectedDateTo: Date | null = null;
  // This method is used to initialize the class instance using fb, router, api, shared service, and lov service
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private api: ApiService,
    private sharedService: SharedService,
    private lovService: LovSelectionService,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private lookupService: LookupService
  ) {
    this.userType = this.sharedService.getPrimaryAccount()['Account Type Code'];
    this.lang = this.sharedService.getCurrentLang();
    this.filterForm = this.fb.group({
      service: [null],
      subService: [null],
      membership: [null],
      status: [null],
      user: [null],
      // dateFrom: this.fb.group({
      //   day: [null],
      //   month: [null],
      //   year: [null]
      // }),
      // dateTo: this.fb.group({
      //   day: [null],
      //   month: [null],
      //   year: [null]
      // }),
      days: ["30"],
      search: '',
      paymentMethod: [this.lang == 'ar' ? FieldValue.PAYMENT_METHOD_ADVANCE_FOR_SERVICES_AR : FieldValue.PAYMENT_METHOD_ADVANCE_FOR_SERVICES]
    });
    this.accountCSN = this.sharedService.getCsnAccountId('csn'); //7093
    this.accountId = this.sharedService.getCsnAccountId('id'); //7093
    let accounts = Utils.checkIsArray(this.sharedService.getUserProfile()?.Account) ?? [];
    accounts?.map((u: AccountModel) => {
      if (u['DC Name English']) {
        let obj = {
          Name: u['CSN']
            ? `${u['DC Name English']} (${u['CSN']})`
            : `${u['DC Name English']}`,
          Value: u['CSN'] ? u['CSN'] : u['Id'],
          isCSN: !!u['CSN']
        };
        this.users.push(obj);
      }
    })
    this.filterForm.get("user")?.setValue(this.accountCSN ? this.accountCSN : this.accountId);
    this.initializePageContext();
  }

  // This method is used to handle the on init lifecycle hook
  ngOnInit(): void {
  }

  // This method is used to search
  search() {
    this.pageIndex = 1;
    this.getPaymentsList();
  }
  // This method is used to remove search using event
  removeSearch(event: KeyboardEvent) {
    const value = this.filterForm.get('search')?.value?.trim() || '';
    if (value === '') {
      this.search();
    }
  }

  // This method is used to handle service change event using option
  onServiceChange(option: any) {
    this.lovService.setLovSelection(option.lovType, option.selectedLov);
    this.filterForm.get("subService")?.setValue(null);
    this.getPaymentsList();
  }
  showCustomDateRange: boolean = false;
  onDayChnage(option: any) {
    if (this.initialLoad) {
      return;
    }
    this.pageIndex = 1;
    if (option.selectedLov?.Value == 'custom') {
      this.showCustomDateRange = true;
    } else {
      this.selectedDateFrom = null;
      this.selectedDateTo = null;
      this.showCustomDateRange = false;
      this.getPaymentsList();
    }
  }
  // This method is used to apply filter using option
  applyFilter(option: any) {
    if (this.initialLoad) {
      return;
    }
    this.pageIndex = 1;
    this.getPaymentsList();
  }
  // This method is used to filter by user using option
  filterByUser(option: any) {
    this.accountCSN = "";
    if (option && option?.selectedLov?.isCSN) {
      this.accountCSN = option.selectedLov && option.selectedLov?.Value ? option.selectedLov.Value : this.sharedService.getCsnAccountId('csn');
    } else {
      this.accountId = option?.selectedLov && option.selectedLov?.Value ? option.selectedLov.Value : this.sharedService.getCsnAccountId('id');
    }
    this.pageIndex = 1;
    this.getPaymentsList();
  }

  toggleDateCalendar(target: 'from' | 'to') {
    if (target === 'from') {
      this.showDateFromCalendar = !this.showDateFromCalendar;
      this.showDateToCalendar = false;
      return;
    }
    this.showDateToCalendar = !this.showDateToCalendar;
    this.showDateFromCalendar = false;
  }

  clearDateFrom(event?: MouseEvent) {
    event?.stopPropagation();
    this.selectedDateFrom = null;
    if (this.selectedDateTo) {
      this.selectedDateTo = null;
    }
    this.showDateFromCalendar = false;
    this.applyFilter(null);
  }

  clearDateTo(event?: MouseEvent) {
    event?.stopPropagation();
    this.selectedDateTo = null;
    this.showDateToCalendar = false;
    this.applyFilter(null);
  }

  onDateFromPicked(date: Date) {
    this.selectedDateFrom = date;
    this.showDateFromCalendar = false;
    this.selectedDateTo = this.getDefaultDateToFromDateFrom(date);
    this.applyFilter(null);
  }

  onDateToPicked(date: Date) {
    this.selectedDateTo = date;
    this.showDateToCalendar = false;
    this.applyFilter(null);
  }

  get dateFromDisplay(): string {
    return this.formatDateDisplay(this.selectedDateFrom);
  }

  get dateToDisplay(): string {
    return this.formatDateDisplay(this.selectedDateTo);
  }

  get maxDateToSelection(): Date | null {
    if (!this.selectedDateFrom) {
      return this.today;
    }
    const fromPlusOneYear = new Date(this.selectedDateFrom);
    fromPlusOneYear.setFullYear(fromPlusOneYear.getFullYear() + 1);
    fromPlusOneYear.setHours(0, 0, 0, 0);

    const today = new Date(this.today);
    today.setHours(0, 0, 0, 0);
    return fromPlusOneYear < today ? fromPlusOneYear : today;
  }

  private formatDateDisplay(date: Date | null): string {
    if (!date) {
      return '';
    }
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  private buildRequestPayload(pageSize: number, startRowNum: number, newQuery: boolean): PaymentStatementRequest {
    this.request = new PaymentStatementRequest();
    const { body } = this.request;
    const invoice = body.IncomingHierarchy.ListOfDCInvoiceStatIO["FS Invoice"];

    body.IncomingHierarchy.MessageId = Utils.generateUniqueId();
    body.Language = this.lang === 'ar' ? 'ARA' : 'ENU';
    body.PageSize = pageSize.toString();
    body.StartRowNum = startRowNum;
    body.NewQuery = newQuery ? "true" : "false";
    body.NoDays = this.filterForm.get('days')?.value ? (this.filterForm.get('days')?.value == 'custom' ? '' : this.filterForm.get('days')?.value) : "180";
    if (this.isStatementsPage && this.showCustomDateRange) {
      body.FromDate = this.formatDateForRequestFromDate(this.selectedDateFrom);
      const effectiveToDate = this.selectedDateTo
        ? this.selectedDateTo
        : (this.selectedDateFrom ? this.getDefaultDateToFromDateFrom(this.selectedDateFrom) : new Date());
      body.ToDate = this.formatDateForRequestFromDate(effectiveToDate);
    } else {
      body.FromDate = "";
      body.ToDate = "";
    }

    invoice["DC Member Num"] = this.accountCSN ? this.accountCSN : this.accountId;
    invoice["DC SRType"] = this.filterForm.get('service')?.value ? this.filterForm.get('service')?.value : '';
    invoice["DC SRSubType"] = this.filterForm.get('subService')?.value || '';
    invoice["Status"] = this.filterForm.get('status')?.value ? this.filterForm.get('status')?.value : '';
    if (this.isStatementsPage) {
      invoice['Payment Method'] = this.filterForm.get('paymentMethod')?.value ? this.filterForm.get('paymentMethod')?.value : '';
    }
    return this.request;
  }

  private formatDateForRequestFromDate(date: Date | null): string {
    if (!date) {
      return "";
    }
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = String(date.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  }

  private getDefaultDateToFromDateFrom(fromDate: Date): Date {
    const today = new Date(this.today);
    today.setHours(0, 0, 0, 0);
    if (fromDate.getFullYear() === today.getFullYear()) {
      return today;
    }
    const fromPlusOneYear = new Date(fromDate);
    fromPlusOneYear.setFullYear(fromPlusOneYear.getFullYear() + 1);
    fromPlusOneYear.setHours(0, 0, 0, 0);
    return fromPlusOneYear;
  }

  private hasValidPaymentResponse(response: any): boolean {
    const hasNoErrorCode = response?.["Error Code"] == null || response?.["Error Code"] === "";
    const hasNoErrorMessage = response?.["Error Message"] == null || response?.["Error Message"] === "";
    const hasInvoices = !!response?.SiebelMessage?.['FS Invoice'];
    return hasNoErrorCode && hasNoErrorMessage && hasInvoices;
  }

  // This method is used to get payments list
  getPaymentsList() {
    if (!this.accountCSN && !this.accountId) {
      return
    }
    this.loader = true;
    this.totalRecords = 0;
    this.payments = [];

    const payload = this.buildRequestPayload(
      Number(this.pageSize),
      Number(this.pageSize) * (this.pageIndex - 1),
      this.pageIndex === 1
    );

    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_ALL_PAYLIST, payload)
      .subscribe({
        next: (response: any) => {
          this.loader = false;
          if (this.hasValidPaymentResponse(response)) {
            this.totalRecords = response.TotalRecords
            this.payments = Utils.checkIsArray(response?.SiebelMessage['FS Invoice']);
          }
          this.initialLoad = false
        },
        error: () => {
          this.loader = false;
        }
      })
  }

  // This method is used to page change using page
  pageChange(page: any) {
    this.selectedRows = [];
    this.pageIndex = page;
    this.getPaymentsList();
  }

  selectedRows: any = [];
  // This method is used to select row using rows
  selectRow(rows: any) {
    this.selectedRows = rows;
  }

  // This method is used to handle the on destroy lifecycle hook
  ngOnDestroy(): void {
  }

  // This method is used to export csv
  exportCSV() {
    if (!this.accountCSN) {
      return;
    }
    this.loader = true;
    const payload = this.buildRequestPayload(this.exportPageSize, 0, true);

    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_ALL_PAYLIST, payload)
      .subscribe({
        next: (response: any) => {
          this.loader = false;
          if (!this.hasValidPaymentResponse(response)) {
            return;
          }

          const exportData = Utils.checkIsArray(response?.SiebelMessage['FS Invoice']);
          if (!exportData.length) {
            return;
          }

          const isStatements = this.isStatementsPage;
          const exportHeadings = isStatements
            ? this.getStatementHeadings().map(key => this.translate.instant(key))
            : [
              'PAYMENTS.COL.APPLICATION',
              'PAYMENTS.COL.INVOICE_NUMBER',
              'PAYMENTS.COL.SERVICE',
              'PAYMENTS.DC_SRSUBTYPE',
              'PAYMENTS.COL.TRANSACTION_DATE',
              'PAYMENTS.COL.COMPANY',
              'PAYMENTS.COL.STATUS'
            ].map(key => this.translate.instant(key));
          const exportKeys = isStatements
            ? this.getStatementDataKeys()
            : [
              'SR Number',
              'DC Parent Invoice Number',
              'DC SRType',
              'DC SRSubType',
              'Invoice Date',
              'DC Name English',
              'Status'
            ];

          const worksheetData = [
            exportHeadings,
            ...exportData.map((row: any) =>
              exportKeys.map(key => {
                if (!isStatements && key === 'Status') {
                  return this.translate.instant('STATUS.CLOSED');
                }
                return row[key] ?? '';
              })
            )
          ];

          const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, isStatements ? 'Statements' : 'Payments');

          const fileName = `${isStatements ? 'statements' : 'payments'}_${new Date().toISOString().split('T')[0]}.xlsx`;
          XLSX.writeFile(workbook, fileName);
        },
        error: () => {
          this.loader = false;
        }
      });
  }

  paymentDetailModal: boolean = false;
  invoiceData: any = ""
  // This method is used to view app using sr
  viewApp(sr: any) {
    sr["SR Type"] = sr["DC SRType"];
    sr["SR Sub Type"] = sr["DC SRSubType"];
    this.sharedService.gotoDetailPage(sr);
    // this.invoiceData = sr;
    // this.paymentDetailModal = true;
  }

  // This method is used to goto payment page using application
  gotoPaymentPage(application: any) {
    this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.PAYMENT}/${application["SR Number"]}`]);
  }

  private initializePageContext(): void {
    this.isStatementsPage = this.route.snapshot.data?.['type'] === 'statements';
    this.pageTitleKey = this.isStatementsPage ? 'MENU_STATEMENT_OF_ACCOUNT' : 'PAYMENTS.TITLE';
    if (this.isStatementsPage) {
      this.getPaymentMethodList()
      this.dateRanges = StatementDaysList;
      this.getPaymentsList();
      this.tableHeadings = this.getStatementHeadings();
      this.tableDataKeys = this.getStatementDataKeys();
      this.showTableActions = false;
      this.showTableActionMore = false;
      this.showTableActionPay = false;
      return;
    }
    this.tableHeadings = this.getPaymentHeadings();
    this.tableDataKeys = this.getPaymentDataKeys();
    this.showTableActions = true;
    this.showTableActionMore = true;
    this.showTableActionPay = true;
  }

  private getPaymentHeadings(): string[] {
    return [
      'PAYMENTS.COL.APPLICATION',
      'PAYMENTS.STATEMENT_COL.RECEIPT_NUMBER',
      'PAYMENTS.COL.SERVICE',
      'PAYMENTS.COL.TRANSACTION_DATE',
      'PAYMENTS.COL.COMPANY',
      'PAYMENTS.COL.STATUS'
    ];
  }

  private getPaymentDataKeys(): string[] {
    return [
      'SR Number',
      'DC Parent Invoice Number',
      'DC SRType',
      'Invoice Date',
      'DC Name English',
      'Status'
    ];
  }

  private getStatementHeadings(): string[] {
    const headingKeys = [
      'PAYMENTS.STATEMENT_COL.RECEIPT_NUMBER',
      'PAYMENTS.STATEMENT_COL.SR_NUMBER',
      'PAYMENTS.STATEMENT_COL.INVOICE_NUMBER',
      'PAYMENTS.STATEMENT_COL.INVOICE_DATE',
      'PAYMENTS.STATEMENT_COL.PAYMENT_METHOD',
      'PAYMENTS.STATEMENT_COL.USER_NAME',
      'PAYMENTS.STATEMENT_COL.EXPORTER_NAME_EN',
      'PAYMENTS.STATEMENT_COL.DESTINATION',
      'PAYMENTS.STATEMENT_COL.OPENING_BALANCE',
      'PAYMENTS.STATEMENT_COL.DEBIT',
      'PAYMENTS.STATEMENT_COL.CREDIT',
      'PAYMENTS.STATEMENT_COL.CLOSING_BALANCE'
    ];
    return headingKeys;
  }

  private getStatementDataKeys(): string[] {
    return [
      'DC Parent Invoice Number',
      'SR Number',
      'DC COO InvoiceNumber',
      'Invoice Date',
      'Payment Method',
      'SR Created Login',
      'Exporter Name English',
      'DC COO Final Destn',
      'DC Initial Amount',
      'DC Debit',
      'DC Credit',
      'DC Balance Amount'
    ];
  }
  paymentMethodList: any = [];
  getPaymentMethodList() {
    this.lookupService.post(LookupKey.LOOKUP_URL, {
      "ProcessName": "DC Get List Of Values WF",
      "Type": LookupKey.PAYMENT_METHOD_CODE,
      "LanguageCode": this.lang == 'ar' ? 'ARA' : 'ENU',
      "ParentValue": "",
      "Sub Type": ""
    })
      .subscribe({
        next: (response: any) => {
          if (response['Error Message'] == null && response.SiebelMessage?.['List Of Values']) {
            const excludedNames = [
              'Credit Facility',
              'Credit Card',
              'DAFZA',
              'DED',
              'JAFZA',
              'Kiosk',
              'Noqodi Pay',
              'Noqodi Wallet'
            ];

            const data = Utils.checkIsArray(
              response.SiebelMessage?.['List Of Values']
            );

            this.paymentMethodList = data.filter(
              (item: any) => !excludedNames.includes(item?.Name)
            );
          }
        }
      })
  }
  downloadReceipts() {
    const receipts = Utils.checkIsArray(this.selectedRows)
      .map((row: any) => row?.['DC eReceipt Download URL'])
      .filter((url: unknown): url is string => Utils.checkIsValidURL(url));

    if (!receipts.length) {
      return;
    }

    receipts.forEach((url, index) => {
      const link = document.createElement('a');
      link.href = url;
      link.download = Utils.extractFileNameFromUrl(url) || `receipt-${index + 1}`;
      link.rel = 'noopener';
      link.target = '_blank';
      link.click();
      link.remove();
    });
  }
}
